import { toast } from 'sonner';
import { Check, Loader2 } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { cn } from '@/lib/utils';
import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/lib/auth/AuthContext';
import { CopyButton } from '@/components/ui/copy-button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

type Config = {
  configured: boolean;
  connection: Connection | null;
  credentials: Credentials;
  last_import_at: string | null;
};

type Connection = {
  enabled: boolean;
  id: string;
  phone_number: string;
  phone_number_id: string;
  webhook_path: string;
  webhook_url: string;
};

type Credentials = {
  api_key_set: boolean;
  updated_at: string | null;
  webhook_secret_set: boolean;
  workspace_id: string | null;
};

export function QuoSettings() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const base = `/api/v1/organizations/${orgId}/quo`;
  const cache = useQueryClient();

  const config = useQuery<Config>({
    enabled: !!orgId,
    queryKey: ['quo-connection', orgId],
    queryFn: async () => (await clientApi.get(`${base}/connection`)).data,
  });

  const creds = config.data?.credentials;
  const connection = config.data?.connection ?? null;

  const lines = useQuery<Array<{ id: string; number: string }>>({
    retry: false,
    queryKey: ['quo-lines', orgId],
    // Only while choosing. Once a line is bound this would be a pointless call
    // to the vendor on every visit to the page.
    enabled: !!creds?.api_key_set && !connection,
    queryFn: async () => (await clientApi.get(`${base}/lines`)).data,
  });

  const [apiKey, setApiKey] = useState('');
  const [workspace, setWorkspace] = useState('');
  const [secret, setSecret] = useState('');
  const [line, setLine] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    setLine(connection?.phone_number_id ?? '');
    setEnabled(connection?.enabled ?? false);
    setWorkspace(creds?.workspace_id ?? '');
  }, [connection, creds?.workspace_id]);

  async function run(what: string, action: () => Promise<unknown>, success: string) {
    setBusy(what);
    try {
      await action();
      await cache.invalidateQueries({ queryKey: ['quo-connection', orgId] });
      await cache.invalidateQueries({ queryKey: ['quo-lines', orgId] });
      toast.success(success);
      return true;
    } catch (error) {
      // Surface what the server said. The delivered version collapsed three
      // distinct 409s into one generic string, which left an admin with no way
      // to tell "set your timezone first" apart from "that line is taken".
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail || 'Something went wrong. Please try again.');
      return false;
    } finally {
      setBusy('');
    }
  }

  if (config.isError) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert>
            <AlertDescription>Quo settings could not be loaded.</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quo telephone encounters</CardTitle>
        <CardDescription>
          Recorded calls on this clinic&rsquo;s line become draft encounters. A call from a number matching exactly one
          patient attaches to that chart automatically; anything ambiguous waits for staff.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {config.isPending ? (
          <p className="text-sm text-muted-foreground">Loading Quo settings…</p>
        ) : (
          <>
            <Step ready n={1} done={!!creds?.api_key_set} title="Connect your Quo account">
              {creds?.api_key_set ? (
                <p className="text-sm text-muted-foreground">
                  Connected to workspace <span className="font-medium text-foreground">{creds.workspace_id}</span>.
                  Enter a new key below to replace it.
                </p>
              ) : null}
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="quo-api-key">Quo API key</FieldLabel>
                  <Input
                    id="quo-api-key"
                    value={apiKey}
                    type="password"
                    autoComplete="off"
                    placeholder={creds?.api_key_set ? 'Replace the stored key' : 'Paste your Quo API key'}
                    onChange={(e) => setApiKey(e.target.value)}
                  />
                  <FieldDescription>
                    Stored encrypted. It is never shown again and never sent back to this page.
                  </FieldDescription>
                </Field>
                <Field>
                  <FieldLabel htmlFor="quo-workspace">Quo workspace id</FieldLabel>
                  <Input
                    id="quo-workspace"
                    value={workspace}
                    placeholder="The workspace this key belongs to"
                    onChange={(e) => setWorkspace(e.target.value)}
                  />
                </Field>
              </FieldGroup>
              <Button
                disabled={!apiKey || !workspace || !!busy}
                onClick={async () => {
                  const ok = await run(
                    'key',
                    () => clientApi.put(`${base}/credentials`, { api_key: apiKey, workspace_id: workspace }),
                    'Quo account connected',
                  );
                  if (ok) setApiKey('');
                }}>
                {busy === 'key' && <Loader2 className="h-4 w-4 animate-spin" />}
                {creds?.api_key_set ? 'Replace key' : 'Connect'}
              </Button>
            </Step>

            <Step n={2} done={!!connection} ready={!!creds?.api_key_set} title="Choose this clinic's line">
              {connection ? (
                <p className="text-sm">
                  <span className="font-medium">{connection.phone_number}</span>{' '}
                  <span className="text-muted-foreground">
                    · a bound line cannot be reassigned here, by design. Moving it needs an audited migration.
                  </span>
                </p>
              ) : (
                <>
                  <Select value={line} onValueChange={setLine}>
                    <SelectTrigger aria-label="Clinic phone line">
                      <SelectValue placeholder={lines.isPending ? 'Loading lines…' : 'Choose a Quo line'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        {lines.data?.map((x) => (
                          <SelectItem key={x.id} value={x.id}>
                            {x.number}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  {lines.isError && (
                    <FieldDescription>
                      Quo would not list your lines. Check the API key and workspace id above.
                    </FieldDescription>
                  )}
                  <Button
                    disabled={!line || !!busy}
                    onClick={() =>
                      run(
                        'line',
                        () => clientApi.put(`${base}/connection`, { enabled: false, phone_number_id: line }),
                        'Clinic line saved',
                      )
                    }>
                    {busy === 'line' && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save line
                  </Button>
                </>
              )}
            </Step>

            <Step n={3} ready={!!connection} title="Create the webhook in Quo" done={!!creds?.webhook_secret_set}>
              {connection && (
                <>
                  <Field>
                    <FieldLabel>Webhook URL</FieldLabel>
                    <div className="flex items-center gap-2">
                      <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1.5 text-xs">
                        {connection.webhook_url}
                      </code>
                      <CopyButton describedAs="webhook URL" text={connection.webhook_url} />
                    </div>
                    <FieldDescription>
                      In Quo Settings → Webhooks, subscribe to <code>call.recording.completed</code> for{' '}
                      {connection.phone_number}, and paste this URL as the destination.
                    </FieldDescription>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="quo-secret">Signing secret</FieldLabel>
                    <Input
                      id="quo-secret"
                      value={secret}
                      type="password"
                      autoComplete="off"
                      placeholder={creds?.webhook_secret_set ? 'Replace the stored secret' : 'Quo signing secret'}
                      onChange={(e) => setSecret(e.target.value)}
                    />
                    <FieldDescription>
                      In Quo, open the webhook menu and choose Reveal signing secret. Paste its base64 key here.
                    </FieldDescription>
                  </Field>
                  <Button
                    disabled={!secret || !!busy}
                    onClick={async () => {
                      const ok = await run(
                        'secret',
                        () => clientApi.put(`${base}/credentials`, { webhook_secret: secret }),
                        'Signing secret saved',
                      );
                      if (ok) setSecret('');
                    }}>
                    {busy === 'secret' && <Loader2 className="h-4 w-4 animate-spin" />}
                    Save secret
                  </Button>
                </>
              )}
            </Step>

            <Step
              n={4}
              title="Turn imports on"
              done={!!connection?.enabled}
              ready={!!connection && !!creds?.webhook_secret_set}>
              <Field orientation="horizontal">
                <Switch
                  id="quo-enabled"
                  checked={enabled}
                  disabled={!!busy}
                  onCheckedChange={(next) => {
                    setEnabled(next);
                    run(
                      'enabled',
                      () => clientApi.put(`${base}/connection`, { enabled: next, phone_number_id: line }),
                      next ? 'Importing recorded calls' : 'Imports paused',
                    );
                  }}
                />
                <FieldLabel htmlFor="quo-enabled">Import recorded calls</FieldLabel>
              </Field>
              <FieldDescription>
                Only calls placed after this line was connected are imported. Pausing returns a retryable error to Quo
                rather than dropping deliveries, so a short pause loses nothing.
              </FieldDescription>
              <p className="text-sm text-muted-foreground">
                Last successful audio import:{' '}
                {config.data?.last_import_at ? new Date(config.data.last_import_at).toLocaleString() : 'none yet'}
              </p>
            </Step>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Connecting Quo is three steps, and they cannot be collapsed into one form.
 *
 * The API key has to exist before we can list the lines to choose from. The
 * line has to be chosen before there is a connection id, and the connection id
 * is inside the webhook URL. Only once the webhook is created does Quo hand
 * back the signing secret. So the order is forced by the vendor, and the card
 * shows it rather than presenting four fields and letting an admin discover the
 * dependency by hitting errors.
 *
 * Secrets go one way. Neither the API key nor the signing secret is ever
 * returned by the server, so there is nothing to prefill and every field here
 * is either empty or replaced.
 */
function Step({
  n,
  done,
  ready,
  title,
  children,
}: {
  children: ReactNode;
  done: boolean;
  n: number;
  ready: boolean;
  title: string;
}) {
  return (
    <div aria-disabled={!ready} className={cn('space-y-3', !ready && 'pointer-events-none opacity-45')}>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-medium',
            done ? 'border-primary bg-primary text-primary-foreground' : 'text-muted-foreground',
          )}>
          {done ? <Check className="h-3.5 w-3.5" /> : n}
        </span>
        <h3 className="text-sm font-medium">{title}</h3>
      </div>
      <div className="space-y-3 pl-8">{children}</div>
    </div>
  );
}
